import { deleteCookie } from 'cookies-next';

import {
  ApplicationStage,
  ContactStatus,
  Profile,
  Role,
} from '@internal/types/profile';

const WAITLISTED_STATUSES: ReadonlySet<string> = new Set([
  ContactStatus.FOR_FUTURE,
  ContactStatus.DECLINED,
  ContactStatus.UNSUPPORTED_COUNTRY,
]);

export const isWaitlistedProfile = (profile: Profile): boolean =>
  WAITLISTED_STATUSES.has(profile.status_in_system ?? '');

const VETTING_BLOCKED_STATUSES: ReadonlySet<string> = new Set([
  ContactStatus.REFUSED,
  ContactStatus.QUIT,
  ContactStatus.FIRED,
  ContactStatus.DISABLED,
  ContactStatus.DELETE,
]);

export const isVettingBlockedProfile = (profile: Profile): boolean =>
  VETTING_BLOCKED_STATUSES.has(profile.status_in_system ?? '');

export const isApplicationStatusHidden = (profile: Profile): boolean =>
  isVettingBlockedProfile(profile) || isWaitlistedProfile(profile);

export const COMMUNITY_ONBOARDING_COPY =
  'If your experience level fits current client needs, we’ll contact you about community onboarding. No action needed now.';

export const VERIFIED_COMMUNITY_ONBOARDING_COPY = `Your role is verified. ${COMMUNITY_ONBOARDING_COPY}`;

const SELF_APPLY_ROLES: ReadonlySet<string> = new Set([
  'candidate',
  'developer',
]);

export const canApplyToProjects = (
  profile: Profile | null | undefined
): boolean => {
  if (!profile) return false;
  return (
    SELF_APPLY_ROLES.has(profile.role) &&
    !isWaitlistedProfile(profile) &&
    !isVettingBlockedProfile(profile)
  );
};

const PARTNER_ROLES: ReadonlySet<Role> = new Set<Role>([
  'partner manager',
  'partner developer',
]);

export const isPartnerAccount = (
  profile: Pick<Profile, 'role'> | null | undefined
): boolean => Boolean(profile && PARTNER_ROLES.has(profile.role));

export const isOnboardedDev = (
  profile: Pick<Profile, 'lemonId'> | null | undefined
): boolean => Boolean(profile?.lemonId?.length);

const SIGNUP_STAGES: ReadonlySet<string> = new Set([
  ApplicationStage.QualificationStarted,
  ApplicationStage.ResumeConfirmed,
]);

export const isSignupIncomplete = (profile: Profile): boolean =>
  (!profile.linkedin || !profile.job_search_status) &&
  !isOnboardedDev(profile) &&
  (!profile.applicationStage || SIGNUP_STAGES.has(profile.applicationStage));

export const APP_COLORS = {
  lilac: '#F1EFFB',
  lilacSoft: '#F7F6FD',
  lilacDeep: '#EBE8FA',
  lavender: '#B7ACE8',
  lavenderSoft: '#D7D2F1',
  yellow: '#F9F860',
  yellowDeep: '#F4F24A',
  blueSoft: '#F0FAFF',
  blueBorder: '#B9E7FF',
  warningSoft: '#FEF7EC',
  warningBorder: '#F0D19B',
} as const;

export const GRADIENTS = {
  hero:
    'linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 42%, #F2F2FB 58%, ' +
    `${APP_COLORS.lilacDeep} 66%, #E6E2FB 76%, #FBFCD7 90%, #F4F8B6 100%)`,
  mark: `linear-gradient(90deg, ${APP_COLORS.lavenderSoft} 0%, ${APP_COLORS.yellow} 100%)`,
} as const;

export const PAGE_MAX_WIDTH = 1500;

export const PAGE_CONTENT_MAX_WIDTH = 1040;
export const SIDE_NAV_WIDTH = 240;

export const goToLogout = () => {
  deleteCookie('authDomainSSO');
  if (window.location.hostname.endsWith('lemon.io')) {
    deleteCookie('authDomainSSO', { domain: 'lemon.io' });
  }
  window.location.assign(`${process.env.APP_LEMON_URL as string}/logout`);
};

export const scrollbarStyles = {
  '@supports not selector(::-webkit-scrollbar)': {
    scrollbarWidth: 'thin',
    scrollbarColor: '#C9C9C9 transparent',
  },
  '&::-webkit-scrollbar': { width: 12 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#C9C9C9',
    borderRadius: 8,
    border: '3px solid transparent',
    backgroundClip: 'padding-box',
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#AFAFAF' },
};
